import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactSelect from 'react-select';
import { useField } from '@unform/core';

import { SelectContainer, Label, customStyles } from './styles';

const optionPropTypes = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  label: PropTypes.string.isRequired,
});

const propTypes = {
  options: PropTypes.arrayOf(optionPropTypes),
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  isLoading: PropTypes.bool,
};

const defaultProps = {
  placeholder: '',
  noOptionsMessage: '',
  isLoading: false,
};

const InputSelect = ({
  name,
  label,
  noOptionsMessage,
  options,
  placeholder,
  isLoading,
  ...rest
}) => {
  const selectRef = useRef(null);
  const { fieldName, defaultValue = '', registerField, error } = useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectRef.current,
      setValue: (ref, value) => {
        ref.select.setValue(value);
      },
      getValue: ref => {
        if (rest.isMulti) {
          if (!ref.state.value) {
            return [];
          }
          return ref.state.value;
        }
        if (!ref.state.value) {
          return '';
        }
        return ref.state.value.value;
      },
      clearValue: ref => {
        ref.select.clearValue();
      },
    });
  }, [fieldName, registerField, rest.isMulti]);

  /**
   * WORKAROUND
   * Multi select is placing the items not vertically centered on rendering
   * But when the input is focused, the items are centered as they should be
   * so the input is programmatically focused to fix that
   */
  useEffect(() => {
    if (rest.isMulti) {
      selectRef.current.focus();
      selectRef.current.blur();
    }
  }, [rest.isMulti]);

  return (
    <SelectContainer>
      <Label htmlFor="select">{label}</Label>
      <ReactSelect
        defaultValue={defaultValue}
        ref={selectRef}
        id="select"
        options={options}
        className="react-select-container"
        classNamePrefix="react-select"
        placeholder={placeholder}
        isMulti={rest.isMulti}
        noOptionsMessage={() => noOptionsMessage}
        isLoading={isLoading}
        styles={customStyles}
        {...rest}
      />
      {error && (
        <span style={{ color: 'var(--danger)', marginTop: 5, fontWeight: 500 }}>
          {error}
        </span>
      )}
    </SelectContainer>
  );
};

InputSelect.propTypes = propTypes;
InputSelect.defaultProps = defaultProps;

export default InputSelect;
